import React, { useCallback, useEffect, useState } from 'react';
import { Megaphone, Trash2, Eye, EyeOff, Plus, Loader2, Save, X, Upload, Mail, Inbox, FileDown } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_BACKEND_URL;

const SLOTS = [
  { id: 'global-top',            label: 'Global - Top of every page (under nav & ticker)' },
  { id: 'article-mid',           label: 'Article - Mid-story (between paragraphs)' },
  { id: 'home-mid',              label: 'Home - Middle (under Tip Wall)' },
  { id: 'home-bottom',           label: 'Home - Bottom (above newsletter)' },
  { id: 'stories-inline-0',      label: 'Investigations - after 5th story' },
  { id: 'stories-inline-1',      label: 'Investigations - after 10th story' },
  { id: 'stories-inline-2',      label: 'Investigations - after 15th story' },
  { id: 'videos-inline-0',       label: 'Videos - after 4th video' },
  { id: 'videos-inline-1',       label: 'Videos - after 8th video' },
  { id: 'videos-inline-2',       label: 'Videos - after 12th video' },
];

const emptyForm = () => ({
  slot_id: 'global-top',
  image_url: '',
  click_url: '',
  alt: '',
  active: true,
  weight: 1,
  advertiser: '',
});

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
};

const AdsTab = ({ token }) => {
  const [ads, setAds] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [downloadingReport, setDownloadingReport] = useState(null); // ad_id currently being downloaded

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const downloadReport = async (ad) => {
    setDownloadingReport(ad.id);
    try {
      const res = await fetch(
        `${API}/api/admin/ads/${ad.id}/report?days=30`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Could not load report data');
      const data = await res.json();
      const { totals, events, since } = data;

      // Build the PDF - Outlaw editorial style: red header rule, mono details,
      // full event log with a date + time column.
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const w = doc.internal.pageSize.getWidth();

      // Header band
      doc.setFillColor(220, 38, 38);
      doc.rect(0, 0, w, 6, 'F');
      doc.setFillColor(10, 10, 10);
      doc.rect(0, 6, w, 70, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(20);
      doc.text('OUTLAW MEDIA', 40, 34);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(180, 180, 180);
      doc.text('Advertising Performance Report', 40, 52);
      const genDate = new Date().toLocaleString('en-AU', { timeZone: 'Australia/Melbourne' });
      doc.text(`Generated: ${genDate}`, 40, 66);

      // Summary block
      doc.setTextColor(0, 0, 0);
      let y = 110;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('CAMPAIGN SUMMARY', 40, y); y += 6;
      doc.setDrawColor(220, 38, 38);
      doc.setLineWidth(1);
      doc.line(40, y, 200, y); y += 20;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const rows = [
        ['Advertiser', ad.advertiser || '-'],
        ['Placement (slot)', ad.slot_id],
        ['Click destination', ad.click_url],
        ['Reporting range', `${data.range_days} days (since ${new Date(since).toLocaleDateString('en-AU')})`],
        ['Total impressions', String(totals.impressions)],
        ['Total clicks', String(totals.clicks)],
        ['Click-through rate', `${totals.ctr_pct}%`],
      ];
      rows.forEach(([k, v]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(k, 40, y);
        doc.setFont('helvetica', 'normal');
        // truncate long URLs
        const val = String(v).length > 65 ? String(v).slice(0, 62) + '…' : String(v);
        doc.text(val, 180, y);
        y += 16;
      });

      // Event log table
      y += 15;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('EVENT LOG', 40, y); y += 6;
      doc.line(40, y, 200, y); y += 6;

      const tableBody = events.slice(0, 5000).map((e) => {
        const d = new Date(e.ts);
        return [
          d.toLocaleDateString('en-AU', { timeZone: 'Australia/Melbourne' }),
          d.toLocaleTimeString('en-AU', { timeZone: 'Australia/Melbourne' }),
          (e.type || '').toUpperCase(),
          e.ip || '-',
        ];
      });

      autoTable(doc, {
        startY: y + 6,
        head: [['Date', 'Time (AEDT)', 'Event', 'Visitor IP']],
        body: tableBody.length > 0 ? tableBody : [['-', '-', 'No events in range', '-']],
        theme: 'striped',
        headStyles: { fillColor: [20, 20, 20], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
        bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        columnStyles: {
          2: { fontStyle: 'bold', textColor: [180, 30, 30] },
        },
        margin: { left: 40, right: 40 },
      });

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(140, 140, 140);
        doc.text('Outlaw Media · outlawmedia.com.au · Confidential to advertiser', 40, doc.internal.pageSize.getHeight() - 20);
        doc.text(`Page ${p} of ${pageCount}`, w - 80, doc.internal.pageSize.getHeight() - 20);
      }

      const safeName = (ad.advertiser || ad.slot_id).replace(/[^A-Za-z0-9]+/g, '-');
      const dstamp = new Date().toISOString().slice(0, 10);
      doc.save(`outlaw-media-report-${safeName}-${dstamp}.pdf`);
    } catch (e) {
      alert(e.message || 'Could not generate report');
    } finally {
      setDownloadingReport(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, i] = await Promise.all([
        fetch(`${API}/api/admin/ads`, { headers: authHeaders }).then((r) => r.ok ? r.json() : []),
        fetch(`${API}/api/admin/ad-inquiries`, { headers: authHeaders }).then((r) => r.ok ? r.json() : { inquiries: [] }),
      ]);
      setAds(Array.isArray(a) ? a : []);
      setInquiries(i.inquiries || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setError('');
    try {
      const presignRes = await fetch(`${API}/api/admin/ads/presign-upload`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ filename: file.name, content_type: file.type }),
      });
      if (!presignRes.ok) {
        const data = await presignRes.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not prepare upload.');
      }
      const { upload_url, public_url } = await presignRes.json();
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error('Upload failed'));
        xhr.onerror = () => reject(new Error('Upload network error'));
        xhr.send(file);
      });
      setForm((f) => ({ ...f, image_url: public_url }));
    } catch (e) { setError(e.message); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const url = editingId ? `${API}/api/admin/ads/${editingId}` : `${API}/api/admin/ads`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders,
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setForm(emptyForm());
      setEditingId(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const edit = (a) => {
    setForm({
      slot_id: a.slot_id, image_url: a.image_url, click_url: a.click_url,
      alt: a.alt || '', active: a.active !== false, weight: a.weight || 1,
      advertiser: a.advertiser || '',
    });
    setEditingId(a.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this ad?')) return;
    await fetch(`${API}/api/admin/ads/${id}`, { method: 'DELETE', headers: authHeaders });
    await load();
  };

  return (
    <div className="space-y-5" data-testid="ads-tab">
      {/* Create / Edit form */}
      <form onSubmit={submit} className="rounded-lg border border-red-900/30 bg-zinc-900/60 p-5 space-y-3" data-testid="ads-form">
        <div className="flex items-center gap-2">
          {editingId ? <Save className="h-5 w-5 text-red-500" /> : <Plus className="h-5 w-5 text-red-500" />}
          <h3 className="text-xl font-black uppercase tracking-wider">{editingId ? 'Edit Ad' : 'New Ad'}</h3>
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(emptyForm()); }}
                    className="ml-auto inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white">
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Slot</label>
            <select
              value={form.slot_id} onChange={(e) => setForm({ ...form, slot_id: e.target.value })}
              data-testid="ads-form-slot"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            >
              {SLOTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Weight (1–100, higher = more often)</label>
            <input
              type="number" min={1} max={100}
              value={form.weight} onChange={(e) => setForm({ ...form, weight: parseInt(e.target.value || 1, 10) })}
              data-testid="ads-form-weight"
              className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            />
          </div>
        </div>

        <div>
          <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Ad Image</label>
          <div className="flex flex-col sm:flex-row gap-2 mt-1">
            <input
              type="url" required maxLength={500}
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              placeholder="Paste image URL or upload below"
              data-testid="ads-form-image-url"
              className="flex-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
            />
            <label className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white text-[11px] font-black uppercase tracking-widest cursor-pointer whitespace-nowrap">
              <Upload className="h-3.5 w-3.5" />
              {uploading ? `${uploadProgress}%` : 'Upload'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                disabled={uploading}
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="ads-form-upload"
                className="hidden"
              />
            </label>
          </div>
          {form.image_url && (
            <div className="mt-2 inline-block rounded border border-zinc-800 overflow-hidden bg-zinc-950 p-1">
              <img src={form.image_url} alt="" className="max-h-24 max-w-full" />
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Click-through URL</label>
          <input
            type="url" required maxLength={500}
            value={form.click_url} onChange={(e) => setForm({ ...form, click_url: e.target.value })}
            placeholder="https://sponsor.example.com"
            data-testid="ads-form-click-url"
            className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          />
        </div>

        <div>
          <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Alt text (accessibility)</label>
          <input
            type="text" maxLength={160}
            value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })}
            placeholder="What the ad says, for screen readers"
            data-testid="ads-form-alt"
            className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          />
        </div>

        <div>
          <label className="text-[10px] font-black tracking-widest uppercase text-gray-400">Advertiser (for reports)</label>
          <input
            type="text" maxLength={120}
            value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })}
            placeholder="e.g. Vega Stars, ACME Legal…"
            data-testid="ads-form-advertiser"
            className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          />
        </div>

        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox" checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            data-testid="ads-form-active"
            className="h-4 w-4 accent-red-600"
          />
          <span className="text-xs font-black uppercase tracking-widest text-gray-300">Active (eligible to rotate)</span>
        </label>

        {error && (
          <div className="bg-red-950/40 border border-red-500/50 text-red-200 px-3 py-2 rounded text-sm">{error}</div>
        )}

        <button
          type="submit"
          disabled={saving || !form.image_url || !form.click_url}
          data-testid="ads-form-submit"
          className="inline-flex items-center gap-2 px-4 py-2 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 disabled:cursor-not-allowed text-white text-[11px] font-black uppercase tracking-widest"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {editingId ? 'Save Changes' : 'Create Ad'}
        </button>
      </form>

      {/* Active ads */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Megaphone className="h-5 w-5 text-red-500" />
          <h3 className="text-xl font-black uppercase tracking-wider">
            {loading ? 'Loading…' : `${ads.length} Ad${ads.length === 1 ? '' : 's'}`}
          </h3>
        </div>
        {!loading && ads.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-6">No ads yet. Create the first one above.</p>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {ads.map((a) => (
            <div key={a.id} className={`rounded-lg p-3 border flex items-start gap-3 ${a.active ? 'border-emerald-500/40 bg-emerald-950/15' : 'border-zinc-800 bg-zinc-950/50'}`}
                 data-testid={`ad-row-${a.id}`}>
              <img src={a.image_url} alt="" className="w-20 h-16 object-cover rounded shrink-0 bg-zinc-950" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-[10px] font-black tracking-widest uppercase text-red-400">{a.slot_id}</span>
                  {a.advertiser && <span className="text-[10px] font-black tracking-widest uppercase text-white">· {a.advertiser}</span>}
                  <span className="text-[10px] text-gray-500">weight {a.weight}</span>
                  {a.active ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"><Eye className="h-2.5 w-2.5" /> Live</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-zinc-800 text-gray-400 border border-zinc-700"><EyeOff className="h-2.5 w-2.5" /> Off</span>
                  )}
                </div>
                <p className="text-xs text-gray-300 truncate" title={a.click_url}>→ {a.click_url}</p>
                {a.alt && <p className="text-[10px] text-gray-500 truncate">{a.alt}</p>}
                <div className="mt-1.5 flex items-center gap-3 text-[10px] text-gray-400">
                  <span><span className="font-black text-white">{a.impressions || 0}</span> impr</span>
                  <span><span className="font-black text-white">{a.clicks || 0}</span> clicks</span>
                  <span className="text-gray-600">
                    CTR {a.impressions ? ((a.clicks || 0) / a.impressions * 100).toFixed(1) : '0.0'}%
                  </span>
                </div>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <button type="button" onClick={() => edit(a)} className="px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-black uppercase tracking-widest">Edit</button>
                  <button
                    type="button"
                    onClick={() => downloadReport(a)}
                    disabled={downloadingReport === a.id}
                    data-testid={`ads-download-report-${a.id}`}
                    className="px-2 py-1 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 text-white text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1"
                    title="Download advertiser PDF report"
                  >
                    {downloadingReport === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
                    PDF
                  </button>
                  <button type="button" onClick={() => remove(a.id)} className="px-2 py-1 rounded bg-zinc-900 hover:bg-red-900/40 border border-zinc-800 hover:border-red-500/60 text-gray-400 hover:text-red-300 text-[10px] font-black uppercase tracking-widest">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Inquiries inbox */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Inbox className="h-5 w-5 text-red-500" />
          <h3 className="text-xl font-black uppercase tracking-wider">
            Advertise-with-us Inquiries ({inquiries.length})
          </h3>
        </div>
        {inquiries.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">No inquiries yet. Empty ad slots prompt visitors to submit.</p>
        )}
        <div className="space-y-2">
          {inquiries.map((q) => (
            <div key={q.id} className="rounded border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`ad-inquiry-row-${q.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
                <div>
                  <p className="text-sm font-black text-white">
                    {q.name}{q.company ? <span className="text-gray-400 font-normal"> · {q.company}</span> : null}
                  </p>
                  <a href={`mailto:${q.email}`} className="text-[11px] text-red-400 hover:text-red-300 inline-flex items-center gap-1">
                    <Mail className="h-3 w-3" /> {q.email}
                  </a>
                </div>
                <span className="text-[10px] text-gray-500">{fmt(q.created_at)}</span>
              </div>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
                {q.budget && <span>budget: <span className="text-gray-300">{q.budget}</span></span>}
                {q.placement && <span>slot: <span className="text-gray-300">{q.placement}</span></span>}
              </div>
              <p className="text-sm text-gray-200 whitespace-pre-wrap break-words border-l-2 border-red-700/60 pl-2.5">{q.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdsTab;
